import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/LanguageContext';

// ─── API ─────────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

// ─── Bento size sırası ───────────────────────────────────────────────────────
const SIZE_CYCLE = ['large', 'small', 'small', 'medium', 'large', 'medium'];

// ─── Video Card ───────────────────────────────────────────────────────────────
const ProjectCard = ({ project, size, index }) => {
  const { t } = useLanguage();
  const [hovered, setHovered] = useState(false);
  const vidRef = useRef(null);
  const navigate = useNavigate();

  const handleEnter = () => {
    setHovered(true);
    if (vidRef.current) { vidRef.current.play(); }
  };
  const handleLeave = () => {
    setHovered(false);
    if (vidRef.current) { vidRef.current.pause(); vidRef.current.currentTime = 0; }
  };

  // Determine media — prefer video_url, fallback image
  const isVideo = project.video_url && project.video_url.trim() !== '';
  const thumb   = project.image || project.thumbnail || '';

  const colSpan = {
    large:  'md:col-span-8 h-[500px]',
    medium: 'md:col-span-4 h-[500px]',
    small:  'md:col-span-6 h-[400px]',
  }[size] || 'md:col-span-6 h-[400px]';

  // Tags array
  const tags = Array.isArray(project.tags)
    ? project.tags
    : typeof project.tags === 'string'
      ? project.tags.split(',').map(t => t.trim()).filter(Boolean)
      : [];

  return (
    <motion.div
      className={`relative group overflow-hidden bg-[#111] cursor-pointer col-span-1 ${colSpan}`}
      style={{ border: '1px solid rgba(255,255,255,0.08)' }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay: index * 0.05, ease: [0.23, 1, 0.32, 1] }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={() => navigate(`/project/${project.id || project._id}`)}
    >
      {/* Meta top-right */}
      <div className="absolute top-4 right-4 z-30 flex flex-col items-end gap-0.5">
        <span className="font-mono text-[8px] tracking-[0.2em] text-white/30 uppercase">
          ID: {String(index + 1).padStart(3, '0')}
        </span>
        {tags[0] && (
          <span className="font-mono text-[8px] tracking-[0.15em] text-white/30 uppercase">{tags[0]}</span>
        )}
      </div>

      {/* Media */}
      {isVideo ? (
        <video
          ref={vidRef}
          loop muted playsInline
          src={project.video_url}
          className="w-full h-full object-cover transition-all duration-700"
          style={{
            filter: hovered ? 'grayscale(0%) contrast(100%)' : 'grayscale(100%) contrast(115%)',
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
          }}
        />
      ) : (
        <img
          src={thumb || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800'}
          alt={project.title}
          className="w-full h-full object-cover transition-all duration-700"
          style={{
            filter: hovered ? 'grayscale(0%) contrast(100%)' : 'grayscale(80%) contrast(110%)',
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
          }}
        />
      )}

      {/* Static dark overlay when not hovered */}
      {!hovered && (
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 to-transparent z-10 pointer-events-none" />
      )}

      {/* Info overlay */}
      <div
        className="absolute inset-0 z-20 flex flex-col justify-end p-6 md:p-8"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, transparent 60%)',
          opacity: hovered ? 1 : 0.75,
          transition: 'opacity 0.4s ease',
        }}
      >
        <div
          style={{
            transform: hovered ? 'translateY(0)' : 'translateY(12px)',
            transition: 'transform 0.5s cubic-bezier(0.23,1,0.32,1)',
          }}
        >
          {project.client && (
            <p className="font-mono text-[9px] tracking-[0.3em] uppercase mb-2"
               style={{ color: 'var(--accent, #E2FF00)' }}>
              {project.client}
            </p>
          )}
          <h3
            className="font-bebas leading-none tracking-tighter mb-3"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3.5rem)',
              animation: hovered ? 'glitch 0.3s linear infinite' : 'none',
            }}
          >
            {project.title}
          </h3>

          {/* View details row — shows on hover */}
          <div
            style={{
              opacity: hovered ? 1 : 0,
              transition: 'opacity 0.5s ease 0.1s',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <div className="w-10 h-px bg-white/40" />
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-white/70">
              {t('projectsPage', 'view_details', 'Detayları Gör')}
            </span>
          </div>
        </div>
      </div>

      {/* Accent border pulse on hover */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
        style={{
          border: '1px solid var(--accent, #E2FF00)',
          opacity: hovered ? 0.25 : 0,
        }}
      />
    </motion.div>
  );
};

// ─── Contact Button ──────────────────────────────────────────────────────────
const ContactButton = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
  sessionStorage.setItem('scrollTo', '#contact');
  navigate('/');
};

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="font-bebas text-lg tracking-widest uppercase px-10 py-4 transition-all relative overflow-hidden"
      style={{
        border: `1px solid ${hovered ? 'var(--accent)' : 'rgba(255,255,255,0.1)'}`,
        color: hovered ? 'var(--accent)' : 'rgba(255,255,255,0.6)',
        background: 'rgba(255,255,255,0.03)',
        textShadow: hovered ? '0 0 20px var(--accent), 0 0 40px var(--accent)' : 'none',
        boxShadow: hovered ? '0 0 20px rgba(226,255,0,0.15), inset 0 0 20px rgba(226,255,0,0.05)' : 'none',
        animation: hovered ? 'glitch 0.3s linear infinite' : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      {t('projectsPage', 'contact_btn', 'İletişime Geç')}
    </button>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export const ProjectsPage = () => {
  const { t } = useLanguage();
  const [projects, setProjects]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetch(`${API}/projects`)
      .then(r => r.json())
      .then(data => {
        setProjects(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Collect unique tag categories
  const allTags = [...new Set(
    projects.flatMap(p =>
      Array.isArray(p.tags)
        ? p.tags
        : typeof p.tags === 'string'
          ? p.tags.split(',').map(t => t.trim()).filter(Boolean)
          : []
    )
  )];

  const filtered = activeFilter === 'all'
    ? projects
    : projects.filter(p => {
        const tags = Array.isArray(p.tags)
          ? p.tags
          : typeof p.tags === 'string'
            ? p.tags.split(',').map(t => t.trim())
            : [];
        return tags.includes(activeFilter);
      });

  return (
    <>
      {/* Bebas Neue font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
        .font-bebas { font-family: 'Bebas Neue', sans-serif; }
        @keyframes glitch {
          0%   { transform: skew(0deg); }
          20%  { transform: skew(-2deg); }
          40%  { transform: skew(2deg); }
          60%  { transform: skew(-1deg); }
          80%  { transform: skew(1deg); }
          100% { transform: skew(0deg); }
        }
        :root { --accent: #E2FF00; }
      `}</style>

      <div className="min-h-screen bg-[#050505] text-white pt-28 pb-24 px-6 md:px-16 lg:px-24">

        {/* ── Header ── */}
        <motion.header
          className="mb-16 border-b pb-12"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
        >
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            {/* Title */}
            <div>
              <div className="flex items-center gap-3 mb-4"
                   style={{ color: 'var(--accent)', fontSize: '10px', letterSpacing: '0.4em' }}>
                <span className="w-8 h-px" style={{ background: 'var(--accent)' }} />
                <span className="font-mono uppercase">{t('projectsPage', 'subtitle', 'Visual Repository')}</span>
              </div>
              <h1 className="font-bebas leading-[0.85] tracking-tighter uppercase"
                  style={{ fontSize: 'clamp(5rem, 12vw, 10rem)', lineHeight: 0.85 }}>
                {t('projectsPage', 'title1', 'Project')} <br />
                <span style={{ color: 'rgba(255,255,255,0.15)' }}>{t('projectsPage', 'title2', 'Archive')}</span>
              </h1>
            </div>

            {/* Filters */}
            {allTags.length > 0 && (
              <div className="flex flex-wrap gap-x-6 gap-y-3 font-bebas text-lg tracking-widest uppercase"
                   style={{ color: 'rgba(255,255,255,0.35)' }}>
                <button
                  onClick={() => setActiveFilter('all')}
                  className="transition-all relative"
                  style={{ color: activeFilter === 'all' ? 'var(--accent)' : undefined }}
                >
                  {t('projectsPage', 'all_filter', '[ All ]')}
                </button>
                {allTags.map(tag => (
                  <button key={tag} onClick={() => setActiveFilter(tag)}
                    className="transition-all hover:text-white"
                    style={{ color: activeFilter === tag ? 'var(--accent)' : undefined }}>
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>
        </motion.header>

        {/* ── Grid ── */}
        {loading ? (
          <div className="flex items-center justify-center py-40">
            <div className="w-px h-10 bg-white/20"
                 style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-40">
            <p className="font-mono text-xs text-white/20 uppercase tracking-widest">{t('projectsPage', 'not_found', 'Proje bulunamadı')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-8"
               style={{ perspective: '1000px' }}>
            <AnimatePresence>
              {filtered.map((project, i) => (
                <ProjectCard
                  key={project.id || project._id || i}
                  project={project}
                  size={SIZE_CYCLE[i % SIZE_CYCLE.length]}
                  index={i}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* ── Footer CTA ── */}
        {!loading && filtered.length > 0 && (
          <motion.div
            className="mt-32 flex flex-col items-center gap-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <div className="w-px h-24"
                 style={{ background: 'linear-gradient(to bottom, var(--accent), transparent)' }} />
            <p className="font-bebas text-xl tracking-[0.3em] uppercase"
               style={{ color: 'rgba(255,255,255,0.15)' }}>
              {t('projectsPage', 'end_of_archive', 'Arşivin Sonu')}
            </p>
            <ContactButton />
          </motion.div>
        )}
      </div>
    </>
  );
};

export default ProjectsPage;